import { useFonts } from 'expo-font';
import { useEffect, useState } from 'react';
import { SafeAreaProvider } from 'react-native-safe-area-context';
import { Provider } from 'react-redux';
import axios from 'axios';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { actionSetFavoriteDoctors, actionSetFirstLogin, actionSetUser, coreStore, actionShowAlert, actionSetCookies } from '@/redux';
import CacheStorage from '../cache/cache.storage';
import { SocketProvider } from '../providers/socket.provider';
import { Platform } from 'react-native';
import { AppThemeProvider } from '../providers/app.theme.provider';
import { SafeAreaView } from '@/components/base/safe.area.view';
import { useAppDispatch } from '@/hooks/use.redux';
import { SplashView } from '@/components/compose/splash.view';
import { NotificationsProvider } from '../providers/notification-provider/notification.provider';
import { PermissionsProvider } from '../providers/permissions.provider';
import { AlertProvider } from '../providers/alert.provider';
import { ErrorReporterProvider } from '../providers/error.reporter.provider';
import * as Linking from 'expo-linking';
import { getAppVersion } from '../utils';

import { omit, setAxiosInstance } from '@matap/shared';
import { ConferenceProvider } from '../providers/conference.provider';
import { isVersionNewer } from '../utils/compare-versions';
import { UpdateModal } from './modals/update.modal';
import { useConfigs } from '@/hooks/use.configs';
import { AppSpinner } from '@/components/base/app.spinner';
import styled from 'styled-components/native';
import { Box } from '@/components/base/box';
import { KeepAliveProvider } from '../providers/keep.alive.provider';
import { useUser } from '@/hooks/use.user';
import { useUserInteraction } from '@/hooks/use.user.interaction';
import { unlockSafariAudio } from '../utils/play.local.sound';
import { getFileSystem } from '../file-manager';
import { ScreenNamesEnum } from '@/constants';
import { actionSetConfigs } from '@/redux/configs.reducer';
import { LinearGradient } from 'expo-linear-gradient';
import { LinkingOptions, NavigationContainer } from '@react-navigation/native';
import { createNativeStackNavigator } from '@react-navigation/native-stack';
import { ModalNamesEnum } from '@/constants/enums/modal.names.enum';

// 🔥 MAGIC PLATFORM IMPORT
// React Native grabs screens.ts (instant). Web grabs screens.web.ts (code-split).
import {
  PaymentDoneScreen,
  SigninScreen,
  MainScreen,
  DoctorsListScreen,
  DoctorProfileScreen,
  ChatScreen,
  HealthFacilitiesScreen,
  QueueListScreen,
  ChatsHistoryModal,
  VisitsHistoryModal,
  OTPModal,
  PaymentModal,
  CallModal,
  FilePickerModal,
  TransactionsHistoryModal,
  NotificationPreferencesModal,
  ProfileSettingsModal,
  AvailabilityModal,
  PatientSurveyModal,
  EndVisitConfirmationModal,
  TermsAndConditionsModal,
  VideoPlayerModal,
  ImageZoomModal,
  PdfViewModal,
} from './screens';
import { PwaInstallProvider } from '../providers/pwa.install.provider';
import { registerCacheApiCompat, registerFindLastIndexCompat, registerRequestIdleCompat, registerWebrtcPolyfills } from '../utils/polyfills';

const isWebPaymentPage = Platform.OS === 'web' && typeof window !== 'undefined' && window.location.href.includes('payment-done');

registerFindLastIndexCompat();

if (Platform.OS !== 'web') {
  registerWebrtcPolyfills();
} else {
  registerRequestIdleCompat();
  registerCacheApiCompat();

  require('../../public/index.css');
  document.body.style.backgroundColor = '#f9fafa';

  if (!isWebPaymentPage) {
    require('../service-workers/service.worker.registration');
  }
}

axios.defaults.baseURL = process.env.EXPO_PUBLIC_API_URL;
axios.defaults.withCredentials = true;
setAxiosInstance(axios);

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    const message = error.response?.data?.message;
    if ((message && typeof message === 'string') || Array.isArray(message)) {
      coreStore.dispatch(actionShowAlert(Array.isArray(message) ? message.join(' ,') : message));
    }
    return Promise.reject(error);
  },
);

const queryClient = new QueryClient({
  defaultOptions: { queries: { gcTime: 300_000, staleTime: 20_000, retry: false }, mutations: { retry: false } },
});

const GradientBackground = styled(LinearGradient).attrs({
  colors: ['#198b8c', '#c3c9c9'],
  locations: [0.5, 0.5],
})`
  flex: 1;
`;

const WhiteColor = styled(Box)`
  background-color: white;
  flex: 1;
`;

const linking: LinkingOptions<ReactNavigation.RootParamList> = {
  prefixes: ['matap://', 'https://pwa.matap.site', 'https://pwa.mattap.ir'],
  config: {
    screens: { ...ModalNamesEnum, ...omit(ScreenNamesEnum, ['PaymentDone']), 'payment-done': ScreenNamesEnum.PaymentDone },
  },
};

const Stack = createNativeStackNavigator();

// 🔥 THE FIX: A raw JavaScript helper function instead of a React component
const renderModal = (name: string, component: React.ComponentType<any>, options: any) => {
  return <Stack.Screen key={name} name={name} component={component} options={options} initialParams={{ fullScreen: options?.presentation === 'transparentModal' }} />;
};

export default function App() {
  console.log('App() render');
  useEffect(() => {
    if (Platform.OS === 'web' && typeof window !== 'undefined' && !isWebPaymentPage) {
      const hasExtraPath = window.location.pathname !== '/main';
      const hasQueryParams = window.location.search !== '';
      const hasHash = window.location.hash !== '';

      // If there is ANY noise in the URL, strip it completely
      if (hasExtraPath || hasQueryParams || hasHash) {
        console.log('re routing to main');
        window.history.replaceState({}, '', '/main');
      }
    }
  }, []);

  return (
    <SafeAreaProvider>
      <Provider store={coreStore}>
        <QueryClientProvider client={queryClient}>
          <NotificationsProvider>
            <PermissionsProvider>
              <KeepAliveProvider>
                <AppWithTheme />
              </KeepAliveProvider>
            </PermissionsProvider>
          </NotificationsProvider>
        </QueryClientProvider>
      </Provider>
    </SafeAreaProvider>
  );
}

function AppWithTheme() {
  console.log('AppWithTheme() render');
  return (
    <AppThemeProvider>
      <SocketProvider>
        <ErrorReporterProvider>
          <GradientBackground>
            <SafeAreaView>
              <WhiteColor>
                <AlertProvider>
                  <PwaInstallProvider>
                    <Initializer>
                      <NavigationContainer linking={linking} documentTitle={{ enabled: false }}>
                        {/* Suspense does nothing on Native (no delay), but perfectly handles Web lazy chunks! */}
                        <ConferenceProvider>
                          <Stack.Navigator id="RootStack" screenOptions={{ headerShown: false }}>
                            {/* 🔥 STANDARD SCREENS */}
                            <Stack.Screen name={ScreenNamesEnum.MainScreen} component={MainScreen} />
                            <Stack.Screen name={ScreenNamesEnum.SignIn} component={SigninScreen} />
                            <Stack.Screen name={ScreenNamesEnum.DoctorsList} component={DoctorsListScreen} />
                            <Stack.Screen name={ScreenNamesEnum.DoctorProfile} component={DoctorProfileScreen} />
                            <Stack.Screen name={ScreenNamesEnum.ChatScreen} component={ChatScreen} />
                            <Stack.Screen name={ScreenNamesEnum.HealthFacilities} component={HealthFacilitiesScreen} />
                            <Stack.Screen name={ScreenNamesEnum.QueueList} component={QueueListScreen} />
                            <Stack.Screen name={ScreenNamesEnum.PaymentDone} component={PaymentDoneScreen} />

                            {/* 🔥 MODAL SCREENS */}
                            {renderModal(ModalNamesEnum.ChatsHistory, ChatsHistoryModal, { presentation: 'transparentModal', animation: 'none' })}
                            {renderModal(ModalNamesEnum.VisitsHistory, VisitsHistoryModal, { presentation: 'transparentModal', animation: 'none' })}
                            {renderModal(ModalNamesEnum.Otp, OTPModal, { presentation: 'transparentModal', animation: 'none' })}
                            {renderModal(ModalNamesEnum.Payment, PaymentModal, { presentation: 'containedTransparentModal', animation: 'none' })}
                            {renderModal(ModalNamesEnum.Call, CallModal, { presentation: 'transparentModal', animation: 'none' })}
                            {renderModal(ModalNamesEnum.FilePicker, FilePickerModal, { presentation: 'containedTransparentModal', animation: 'none' })}
                            {renderModal(ModalNamesEnum.TransactionsHistory, TransactionsHistoryModal, {
                              presentation: 'transparentModal',
                              animation: 'none',
                            })}
                            {renderModal(ModalNamesEnum.NotificationPreferences, NotificationPreferencesModal, {
                              presentation: 'transparentModal',
                              animation: 'none',
                            })}
                            {renderModal(ModalNamesEnum.ProfileSettings, ProfileSettingsModal, { presentation: 'transparentModal', animation: 'none' })}
                            {renderModal(ModalNamesEnum.Availability, AvailabilityModal, { presentation: 'transparentModal', animation: 'none' })}
                            {renderModal(ModalNamesEnum.PatientSurvey, PatientSurveyModal, { presentation: 'containedTransparentModal', animation: 'none' })}
                            {renderModal(ModalNamesEnum.EndVisitConfirmation, EndVisitConfirmationModal, {
                              presentation: 'containedTransparentModal',
                              animation: 'none',
                            })}
                            {renderModal(ModalNamesEnum.TermsAndConditions, TermsAndConditionsModal, {
                              presentation: 'transparentModal',
                              animation: 'none',
                            })}
                            {renderModal(ModalNamesEnum.VideoPlayer, VideoPlayerModal, { presentation: 'transparentModal', animation: 'none' })}
                            {renderModal(ModalNamesEnum.ImageZoom, ImageZoomModal, { presentation: 'transparentModal', animation: 'none' })}
                            {renderModal(ModalNamesEnum.PdfView, PdfViewModal, { presentation: 'transparentModal', animation: 'none' })}
                          </Stack.Navigator>
                        </ConferenceProvider>
                      </NavigationContainer>
                    </Initializer>
                  </PwaInstallProvider>
                </AlertProvider>
              </WhiteColor>
            </SafeAreaView>
          </GradientBackground>
        </ErrorReporterProvider>
      </SocketProvider>
    </AppThemeProvider>
  );
}

function Initializer({ children }) {
  console.log('Initializer() render');
  const url = Linking.getLinkingURL();
  const isPaymentDeepLink = url?.includes(ScreenNamesEnum.PaymentDone) ?? false;
  const dispatch = useAppDispatch();
  const [ready, setReady] = useState(false);
  const [splashEnded, setSplashEnded] = useState(isPaymentDeepLink);
  const { authenticated } = useUser();
  const { latestVersion } = useConfigs();
  const [updateModalVisible, setUpdateModalVisible] = useState(false);

  // Background Web Preloader (Ignored on Native)
  useEffect(() => {
    if (Platform.OS === 'web') {
      Object.values(require('./screens')).forEach((v: any) => {
        if (v && typeof v.preload === 'function') {
          Promise.resolve(v.preload()).catch((err) => console.error('Failed to preload chunk:', err));
        }
      });
    }
  }, []);

  useEffect(() => {
    axios.interceptors.response.use((response) => {
      const setCookie: string[] = response.headers['set-cookie'] || response.headers['Set-Cookie'];
      if (setCookie) {
        const authCookie = setCookie.find((c) => c.startsWith('user_authorization'));
        dispatch(actionSetCookies(authCookie.split(';')[0]));
      }
      return response;
    });
  }, [dispatch]);

  useUserInteraction(
    () => {
      unlockSafariAudio();
    },
    true,
    true,
  );

  useFonts({
    'Shabnam-FD': require('@/assets/fonts/Shabnam-FD.ttf'),
    'Shabnam-Bold-FD': require('@/assets/fonts/Shabnam-Bold-FD.ttf'),
    Shabnam: require('@/assets/fonts/Shabnam.ttf'),
  });

  useEffect(() => {
    async function load() {
      try {
        const userString = await CacheStorage.get('user-string');
        dispatch(actionSetFirstLogin(((await CacheStorage.get('first-login')) || 'true') === 'true'));
        dispatch(actionSetUser(userString ? JSON.parse(userString) : undefined));
        dispatch(actionSetFavoriteDoctors(JSON.parse((await CacheStorage.get('favourite-doctors')) || '[]')));
        dispatch(actionSetCookies(await CacheStorage.get('cookies')));
        const bannerString = await CacheStorage.get('banners');
        const supportPhoneNumber = await CacheStorage.get('supportPhoneNumber');
        dispatch(
          actionSetConfigs({
            banners: bannerString ? JSON.parse(bannerString) : [],
            supportPhoneNumber,
          }),
        );

        await getFileSystem(true).initialize();
        await getFileSystem(false).initialize();
        await getFileSystem(true).purge();
        setReady(true);
      } catch (e) {
        console.error(e);
      }
    }
    load();
  }, []);

  useEffect(() => {
    if (ready && !authenticated) {
      getFileSystem(false).purge();
      getFileSystem(true).purge();
    }
  }, [ready, authenticated]);

  useEffect(() => {
    if (!ready || !latestVersion) return;
    const currentVersion = getAppVersion();
    if (isVersionNewer(latestVersion.versionCode, currentVersion)) {
      setUpdateModalVisible(true);
    }
  }, [ready, latestVersion]);

  if (isPaymentDeepLink && !ready) {
    return (
      <SplashBox flex={1} alignItems={'center'} justifyContent={'center'}>
        <AppSpinner />
      </SplashBox>
    );
  }

  if ((!ready || !splashEnded) && !isPaymentDeepLink) {
    return <SplashView onSplashEnded={() => setSplashEnded(true)} />;
  }

  if (updateModalVisible && latestVersion?.forceUpdate) {
    return (
      <UpdateModal visible={updateModalVisible} changeLog={latestVersion.changeLog} downloadLink={latestVersion.downloadLink} forceUpdate={latestVersion.forceUpdate} />
    );
  }

  return (
    <>
      {latestVersion && updateModalVisible && (
        <UpdateModal
          visible={updateModalVisible}
          onRequestClose={() => setUpdateModalVisible(false)}
          changeLog={latestVersion.changeLog}
          downloadLink={latestVersion.downloadLink}
          forceUpdate={latestVersion.forceUpdate}
        />
      )}
      {children}
    </>
  );
}

const SplashBox = styled(Box)`
  background-color: white;
`;
